import { MakeLogicType, actions, afterMount, connect, isBreakpoint, kea, listeners, path } from 'kea'

import { featureFlagLogic } from 'lib/logic/featureFlagLogic'
import { teamLogic } from 'scenes/teamLogic'

import { productSetupProbes } from '~/products'

import * as api from 'products/event_definitions/frontend/generated/api'

import type { FeatureFlagsSet } from '../../logic/featureFlagLogic'
import { productSetupStatusLogic } from './productSetupStatusLogic'
import { ProductSetupProbe, statusFromProbeDefinitions } from './setupProbes'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface productSetupPreloadLogicValues {
    featureFlags: FeatureFlagsSet // featureFlagLogic
    currentTeamId: number | null // teamLogic
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface productSetupPreloadLogicActions {
    preloadStatuses: () => {
        value: true
    }
}

export type productSetupPreloadLogicType = MakeLogicType<
    productSetupPreloadLogicValues,
    productSetupPreloadLogicActions
>

/**
 * Resolves every registered product's setup status as early as possible with one
 * event-definition request after the team loads and seeds
 * `productSetupStatusLogic` with the results. By the time a user first opens a
 * gated product scene, the status is usually already known, so the gate renders
 * the empty state (or the scene) directly instead of a loading spinner.
 *
 * The product's own detection logic remains the in-scene source of truth: it
 * re-checks with a fresh (uncached) query and keeps polling. The preload only
 * seeds instances still in `loading`, so it can never override a fresher answer.
 *
 * Best-effort by design: a failed run marks nothing as done, so the next trigger
 * (team load, flags arriving, idle) retries, and a probe is only skipped once it
 * has actually been seeded for the current team.
 */
export const productSetupPreloadLogic = kea<productSetupPreloadLogicType>([
    path(['lib', 'components', 'ProductEmptyState', 'productSetupPreloadLogic']),
    connect(() => ({
        values: [teamLogic, ['currentTeamId'], featureFlagLogic, ['featureFlags']],
    })),
    actions({
        preloadStatuses: true,
    }),
    listeners(({ actions, values, cache }) => ({
        preloadStatuses: async (_, breakpoint) => {
            const teamId = values.currentTeamId
            if (!teamId) {
                return
            }
            // Keyed by team AND probe: a project switch re-seeds for the new team, and a
            // flag-gated probe whose flag arrives late still gets its turn.
            const seeded: Set<string> = (cache.seeded ??= new Set())
            const probes = productSetupProbes.filter(
                (probe) =>
                    (!probe.featureFlag || values.featureFlags[probe.featureFlag]) &&
                    !seeded.has(`${teamId}:${probe.productKey}`)
            )
            if (!probes.length) {
                return
            }

            const eventNames = [
                ...new Set(probes.flatMap((probe) => [...probe.hasDataEvents, ...(probe.waitingEvents ?? [])])),
            ]

            let existingEventNames: Set<string>
            try {
                const response = await api.eventDefinitionsList(String(teamId), {
                    names: eventNames,
                    limit: eventNames.length,
                })
                existingEventNames = new Set(response.results.map(({ name }) => name))
            } catch (error) {
                if (error instanceof Error && isBreakpoint(error)) {
                    throw error
                }
                // Nothing was marked seeded, so the next trigger retries; until then the
                // in-scene detection logic answers on its own.
                return
            }
            breakpoint()
            if (values.currentTeamId !== teamId) {
                return
            }

            for (const probe of probes) {
                seeded.add(`${teamId}:${probe.productKey}`)
                seedProbeStatus(probe, existingEventNames, cache)
            }
        },
        // Covers login and project switches that happen after mount.
        [teamLogic.actionTypes.loadCurrentTeamSuccess]: ({ currentTeam }) => {
            if (currentTeam) {
                actions.preloadStatuses()
            }
        },
        // Flags resolve asynchronously (posthog-js `onFeatureFlags`), possibly after
        // the boot triggers have already run - re-check so flag-gated probes aren't
        // silently skipped for the whole session.
        [featureFlagLogic.actionTypes.setFeatureFlags]: () => {
            actions.preloadStatuses()
        },
    })),
    afterMount(({ actions, cache }) => {
        // Defer to idle so boot-critical work (scene chunk, first queries) wins.
        cache.disposables.add(() => {
            const run = (): void => actions.preloadStatuses()
            if (typeof window.requestIdleCallback === 'function') {
                const id = window.requestIdleCallback(run, { timeout: 5000 })
                return () => window.cancelIdleCallback(id)
            }
            const id = window.setTimeout(run, 1000)
            return () => clearTimeout(id)
        }, 'idle-preload')
    }),
])

function seedProbeStatus(probe: ProductSetupProbe, eventNames: Set<string>, cache: Record<string, any>): void {
    const instance = productSetupStatusLogic.build({ productKey: probe.productKey })
    if (!instance.isMounted()) {
        // Keep the instance alive for the app's lifetime so the seeded status is
        // still there when the scene finally opens. Mounting inside the disposable's
        // setup (with hidden-tab pausing off) is what keeps it alive across tab
        // switches - pausing would unmount it and silently discard the seed.
        cache.disposables.add(
            () => {
                instance.mount()
                return () => instance.unmount()
            },
            `status-${probe.productKey}`,
            { pauseOnPageHidden: false }
        )
    }
    // Seed only unknown statuses - a product logic that already answered
    // (fresh, uncached) always beats this cached approximation.
    if (instance.values.status === 'loading') {
        instance.actions.setDetectedStatus(statusFromProbeDefinitions(probe, eventNames))
    }
}
